import { htmlEscape } from '@edicom/common/string/htmlEscape';

export default {
  setCommonAttributes,
  generateInput,
  generateNumber,
  generateSelect,
  toString: valueToString,
};

function setCommonAttributes(elementName, viewField, engineApi) {
  engineApi.options = engineApi.options || {};
  viewField.metadata = viewField.metadata || {};

  const _fiscalnumber = Object.assign({}, viewField.metadata.fiscalnumber);
  const registerName = engineApi.utils.getParent(viewField.model);
  if (registerName) {
    engineApi._modelsToInit[registerName] = engineApi._modelsToInit[registerName] || false; // @odeniel es siempre 'object' sino se sobreescribira por array en register
  }

  const element = new engineApi.Element(elementName)
    .addAttributes({
      'x-path': viewField.model,
      name: viewField.name || viewField.model, // lo pongo aqui porque de todas formas el ec-form-element lo pone despues (id incluido)
      'ng-model': engineApi.property.getNgModel(viewField),
      label: engineApi.options.hideLabel ? '' : viewField.title,
      'ec-flex': viewField.cols || engineApi.options.cols,
    })
    .addAttributes(
      {
        placeholder: htmlEscape(viewField.placeholder),
        help: htmlEscape(viewField.help) || '',
        'ng-pattern': viewField.pattern ? engineApi.property.getPattern(viewField.pattern) : '',
        'ng-disabled': engineApi.property.getDisabled(viewField, engineApi.options.isReadOnly),
        'ng-required': viewField.required,
        'ec-invalid-validator': viewField.invalid,
        'ec-default': engineApi.property.getDefaultValue(viewField),
        'ec-default-eval': viewField.metadata.defaultExpression,
        'ng-show': engineApi.property.getVisible(viewField),
        'ec-fiscalnumber-validator': _fiscalnumber.country || '',
        'ec-fiscalnumber-validator-watch': _fiscalnumber.watch || '',
      },
      true
    );

  element.addAttribute(
    'fb-hidden',
    JSON.stringify({
      registerName: engineApi.registerName,
      name: viewField.name || viewField.model, // siempre es model menos para fields/registros como "efb-partner-edicom"
    }),
    true,
    "'"
  );

  element.addAttribute('ng-model-options', typeof viewField.metadata.allowInvalid === 'boolean' ? `{ allowInvalid: ${viewField.metadata.allowInvalid} }` : '', true);

  if (engineApi.property.getFbFlash(viewField, viewField.parent)) {
    viewField.fbFlash = true;
    // si existe function flash para el tipo
    element.addAttribute('fb-flash', engineApi.registerFbFlash(viewField.model));
  }

  const parent = engineApi.utils.getParent(viewField.model);
  const isArray = parent.endsWith(']');

  if (isArray) {
    console.warn('DEPRECATED - si vez este mensaje, por favor avisa a @odeniel', elementName);
    element.addAttribute('fb-register-multiple', parent.substring(0, parent.indexOf('[')));
  } else {
    element.addAttribute('fb-register', parent);
  }

  if (viewField.invalid) {
    element.cssClass.push('ng-touched');
  }

  return element;
}

function generateInput(viewField, engineApi, type) {
  const isNumber = type === 'number';
  viewField.metadata = viewField.metadata || {};
  const element = setCommonAttributes(isNumber ? 'ec-number' : 'ec-input', viewField, engineApi)
    .addAttribute('type', type)
    .addAttribute('minlength', viewField.metadata.minLength || '', true)
    .addAttribute('maxlength', viewField.metadata.maxLength || '', true)
    .addAttribute('icon', viewField.metadata.icon || '', true);

  if (!isNumber && viewField.metadata.mask) {
    element
      .addAttribute('mask', viewField.metadata.mask)
      .addAttribute('clean', true)
      .addAttribute('validate', 'false')
      .addAttribute('restrict', 'reject');
  }

  return element;
}

function generateNumber(viewField, engineApi) {
  return generateInput(viewField, engineApi, 'number')
    .addAttribute('min', typeof viewField.metadata.min !== 'undefined' ? viewField.metadata.min : '', true)
    .addAttribute('max', viewField.metadata.max || '', true)
    .addAttribute('decimal', addNumberAttribute(viewField.metadata.decimal), true)
    .addAttribute('step', viewField.metadata.step || '', true);
}

function generateSelect(viewField, engineApi, type) {
  viewField.metadata = viewField.metadata || {};

  const eSelect = engineApi.field
    .setCommonAttributes(type, viewField, engineApi)
    .addAttribute('sort', typeof viewField.metadata.sort !== 'undefined' ? viewField.metadata.sort : true)
    .addAttribute('editable', viewField.metadata.autocomplete || '', true);

  if (viewField.metadata.allowInvalid === false) {
    eSelect
      .addAttribute('ng-model-options', '{allowInvalid: false, debounce:0}') // sin esto no permite poner espacios en los select del grid
      .addAttribute('search', true, true);
  }

  Array.isArray(viewField.metadata.options) ? generateArray() : generateObject();

  function generateArray() {
    // @odeniel si detectamos que el primer valor es de tipo numerico, consideramos que todos lo son y pasamos el select a tipo "number"
    if (viewField.metadata.options.length > 0 && typeof viewField.metadata.options[0].value === 'number') {
      eSelect.addAttribute('type', 'number');
    }

    viewField.metadata.options
      // @amirabal 24/05/2020 Filtro valores nulos, si quieren filtrar los blancos, hacerlo aqui y no con lo comentado abajo
      .filter(function(item) {
        return item !== null;
      })
      .forEach(({ value = '', text = '' }) => {
        // if (value.value !== ""){ // @odeniel 04/06/2019 quito esto porque no veo razón para bloquear valores blancos
        const option = new engineApi.Element('option').addAttribute('value', value).setHtml(text);
        eSelect.appendChild(option);
        // }
      });
  }

  function generateObject() {
    Object.keys(viewField.metadata.select || {}).forEach(value => {
      const option = new engineApi.Element('option').addAttribute('value', value).setHtml(viewField.metadata.select[value] || '');
      eSelect.appendChild(option);
    });
  }

  return eSelect;
}

function valueToString(viewField) {
  viewField.metadata = viewField.metadata || {};
  return value => {
    try {
      if (viewField.type === 'string') {
        return htmlEscape(value);
      }

      if (viewField.type === 'number') {
        if (!edicom) {
          return value;
        }
        const formatType = viewField.metadata.mask ?? 'number';
        const params = {};
        if (!isNaN(parseInt(viewField.metadata.decimal))) {
          params.decimal = parseInt(viewField.metadata.decimal);
        }
        if (viewField.metadata.currency) {
          params.currency = viewField.metadata.currency;
        }
        return edicom.locale[formatType](value, params);
      }

      if (viewField.type === 'select') {
        if (viewField.metadata.select) {
          return viewField.metadata.select[value] ?? '';
        }
      }
    } catch (e) {
      console.error(e);
    }

    // si cualquier problema, no queremos pintar undefined o lo que sea al usuario, devolvemos:
    return '';
  };
}

function addNumberAttribute(attribute) {
  if (typeof attribute === 'number') {
    return attribute;
  }
  if (typeof attribute === 'string') {
    return `{{${attribute}}}`;
  }
  return '';
}
